import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { KnoxDialog } from '@/components/ui/Dialog';
import RecaptchaCheckbox from '@/components/RecaptchaCheckbox';
import { loadCaptcha, executeCaptcha } from '@/lib/recaptcha';
import { runtimeCaptchaConfig, checkboxSiteKey, isCn } from '@/lib/captcha-config';
import { knoxApi } from '@/lib/knox-api';
import { useAuth } from '@/lib/auth';

const CAPTCHA_CFG = runtimeCaptchaConfig();
const RECAPTCHA_CHECKBOX_KEY = checkboxSiteKey() ?? '';

const ROLE_OPTIONS = [
  'itOperations',
  'platformEngineering',
  'sre',
  'infrastructure',
  'engineeringManagement',
  'other',
] as const;

const ROLE_VALUES: Record<string, string> = {
  itOperations: 'IT Operations',
  platformEngineering: 'Platform Engineering',
  sre: 'SRE',
  infrastructure: 'Infrastructure',
  engineeringManagement: 'Engineering Management',
  other: 'Other',
};

const TEAM_SIZE_OPTIONS = ['1-5', '6-20', '21-50', '51-200', '200+'] as const;

interface Props {
  open: boolean;
  onClose: () => void;
}

export function AccessApplicationDialog({ open, onClose }: Props) {
  const { t } = useTranslation('knox-aiops');
  const { user } = useAuth();

  const isLoggedIn = !!user?.user_id;
  const userEmail = user?.email?.trim() || '';
  const userName = user?.name?.trim() || '';
  const needsEmailInput = !isLoggedIn || !userEmail;
  const needsNameInput = !isLoggedIn || !userName;

  const [workEmail, setWorkEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [role, setRole] = useState('');
  const [problem, setProblem] = useState('');
  const [teamSize, setTeamSize] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [needChallenge, setNeedChallenge] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (open && CAPTCHA_CFG) {
      loadCaptcha(CAPTCHA_CFG).catch(() => {});
    }
  }, [open]);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (needsEmailInput) {
      if (!workEmail.trim()) errors.workEmail = t('accessApplication.required');
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(workEmail.trim()))
        errors.workEmail = t('accessApplication.invalidEmail');
    }
    if (needsNameInput) {
      if (!fullName.trim()) errors.fullName = t('accessApplication.required');
    }
    if (!companyName.trim()) errors.companyName = t('accessApplication.required');
    if (!role) errors.role = t('accessApplication.required');
    if (!problem.trim()) errors.problem = t('accessApplication.required');
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submitWithToken = useCallback(
    async (recaptchaToken?: string, recaptchaType?: 'checkbox') => {
      setLoading(true);
      setError(null);
      try {
        const resp = await knoxApi.submitAccessApplication(
          {
            workEmail: needsEmailInput ? workEmail.trim() : userEmail,
            fullName: needsNameInput ? fullName.trim() : userName,
            companyName: companyName.trim(),
            role: ROLE_VALUES[role] || role,
            problem: problem.trim(),
            teamSize: teamSize || undefined,
          },
          { recaptchaToken, recaptchaType },
        );
        setSuccess(true);
        setSuccessMessage(resp.message || t('accessApplication.successMessage'));
      } catch (err: any) {
        if (err?.code === 'NEED_CHALLENGE') {
          setNeedChallenge(true);
        } else if (err?.code === 'RATE_LIMITED') {
          setError(t('accessApplication.rateLimited'));
        } else if (Array.isArray(err?.details) && err.details.length > 0) {
          const validationErrors = Object.fromEntries(
            err.details
              .filter((detail: unknown) => (
                typeof detail === 'object'
                && detail !== null
                && typeof (detail as { field?: unknown }).field === 'string'
                && typeof (detail as { message?: unknown }).message === 'string'
              ))
              .map((detail: { field: string; message: string }) => [detail.field, detail.message]),
          );
          setFieldErrors(validationErrors);
        } else {
          setError(err?.message || t('accessApplication.genericError'));
        }
      } finally {
        setLoading(false);
      }
    },
    [needsEmailInput, needsNameInput, userEmail, userName, workEmail, fullName, companyName, role, problem, teamSize, t],
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    let token: string | undefined;
    if (CAPTCHA_CFG) {
      try {
        token = await executeCaptcha('access_application');
      } catch {
        // proceed without token — backend will reject if required
      }
    }
    await submitWithToken(token);
  };

  const handleCheckboxVerified = (token: string) => {
    setNeedChallenge(false);
    submitWithToken(token, 'checkbox');
  };

  const handleClose = () => {
    setSuccess(false);
    setError(null);
    setFieldErrors({});
    setNeedChallenge(false);
    onClose();
  };

  const inputClass =
    'w-full rounded-lg border border-black/[0.16] bg-surface-container-lowest py-2.5 px-4 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-primary/30 focus:outline-none focus:ring-2 focus:ring-primary/10 transition-all';
  const selectClass = `${inputClass} appearance-none`;
  const labelClass = 'block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5';

  if (success) {
    return (
      <KnoxDialog open={open} onClose={handleClose} title={t('accessApplication.successTitle')} mobileScrollable>
        <div className="flex flex-col items-center gap-4 py-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
            <svg className="h-7 w-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-sm text-muted-foreground text-center max-w-[320px]">
            {successMessage}
          </p>
          <p className="text-xs text-muted-foreground/60">
            {t('accessApplication.checkInbox')}
          </p>
          <button
            type="button"
            onClick={handleClose}
            className="mt-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white hover:bg-primary/90 transition-colors"
          >
            {t('accessApplication.close')}
          </button>
        </div>
      </KnoxDialog>
    );
  }

  return (
    <KnoxDialog open={open} onClose={handleClose} title={t('accessApplication.title')} mobileScrollable>
      <div className="pr-1">
        <p className="text-sm text-muted-foreground mb-5">
          {t('accessApplication.subtitle')}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {needsEmailInput && (
            <div>
              <label className={labelClass}>{t('accessApplication.workEmail')}</label>
              <input
                type="email"
                value={workEmail}
                onChange={(e) => setWorkEmail(e.target.value)}
                placeholder={t('accessApplication.workEmailPlaceholder')}
                className={inputClass}
              />
              {fieldErrors.workEmail && (
                <p className="mt-1 text-xs text-destructive">{fieldErrors.workEmail}</p>
              )}
            </div>
          )}

          {needsNameInput && (
              <div>
                <label className={labelClass}>{t('accessApplication.fullName')}</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={t('accessApplication.fullNamePlaceholder')}
                  className={inputClass}
                />
                {fieldErrors.fullName && (
                  <p className="mt-1 text-xs text-destructive">{fieldErrors.fullName}</p>
                )}
              </div>
          )}

          <div>
            <label className={labelClass}>{t('accessApplication.companyName')}</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder={t('accessApplication.companyNamePlaceholder')}
              className={inputClass}
            />
            {fieldErrors.companyName && (
              <p className="mt-1 text-xs text-destructive">{fieldErrors.companyName}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>{t('accessApplication.role')}</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className={selectClass}
            >
              <option value="">{t('accessApplication.rolePlaceholder')}</option>
              {ROLE_OPTIONS.map((key) => (
                <option key={key} value={key}>
                  {t(`accessApplication.roleOptions.${key}`)}
                </option>
              ))}
            </select>
            {fieldErrors.role && (
              <p className="mt-1 text-xs text-destructive">{fieldErrors.role}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>{t('accessApplication.problem')}</label>
            <textarea
              value={problem}
              onChange={(e) => setProblem(e.target.value)}
              placeholder={t('accessApplication.problemPlaceholder')}
              rows={3}
              className={`${inputClass} resize-none`}
            />
            {fieldErrors.problem && (
              <p className="mt-1 text-xs text-destructive">{fieldErrors.problem}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>{t('accessApplication.teamSize')}</label>
            <select
              value={teamSize}
              onChange={(e) => setTeamSize(e.target.value)}
              className={selectClass}
            >
              <option value="">{t('accessApplication.teamSizePlaceholder')}</option>
              {TEAM_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          {needChallenge && !isCn() && RECAPTCHA_CHECKBOX_KEY && (
            <RecaptchaCheckbox
              siteKey={RECAPTCHA_CHECKBOX_KEY}
              action="access_application"
              onVerified={handleCheckboxVerified}
            />
          )}

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-primary px-5 py-3 text-sm font-bold text-white transition-all hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('accessApplication.submitting') : t('accessApplication.submit')}
          </button>
        </form>
      </div>
    </KnoxDialog>
  );
}
